import { useState, type ReactNode } from 'react';
import {
  REFERENCE_KIND_LABELS,
  REFERENCE_KINDS,
  type ReferenceKind,
  type ReferenceTermView,
} from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { errorMessage } from '@/shared/http/http-client';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  Kicker,
  Muted,
  Segmented,
  Skeleton,
} from '@/shared/ui';
import styles from './admin.module.scss';

/**
 * Régie — la tenue du référentiel.
 *
 * Tout membre enrichit le référentiel en séance, sans attendre personne : c'est ce qui
 * permet de finir une note quand « Grogue » n'était pas prévu. La contrepartie est qu'il
 * s'y dépose, avec le temps, des variantes que la normalisation ne rattrape pas —
 * « Brut de fût » et « Brut de fut sans accent » se ressemblent pour un lecteur, pas pour
 * un slug.
 *
 * Cet écran est l'entretien. Renommer corrige une orthographe ; fusionner absorbe un
 * terme dans un autre et **réécrit les fiches et les notes qui le portaient**. La seconde
 * opération est destructive et sans historique, d'où la confirmation nommée et le compte
 * d'emplois affiché en clair : on ne fusionne pas à l'aveugle un terme employé douze fois.
 */
export function AdminReferenceScreen(): ReactNode {
  const api = useApi();
  const [kind, setKind] = useState<ReferenceKind>('COUNTRY');
  const [error, setError] = useState<string | null>(null);

  const terms = useAsync(() => api.reference.list(kind), [kind]);

  async function act(action: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await action();
      terms.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  const list = terms.data ?? [];
  const orphans = list.filter((term) => term.usageCount === 0).length;

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Régie</Kicker>
        <h1>Le référentiel du cercle</h1>
        <Muted>
          Chacun l’enrichit en séance ; on l’entretient ici. Renommer corrige une
          orthographe, fusionner réécrit les fiches.
        </Muted>
      </header>

      <ErrorText>{error}</ErrorText>

      <Segmented
        label="Nature du terme"
        value={kind}
        onChange={setKind}
        options={REFERENCE_KINDS.map((value) => ({
          value,
          label: REFERENCE_KIND_LABELS[value],
        }))}
      />

      {terms.loading ? <Skeleton lines={5} /> : null}
      {!terms.loading && list.length === 0 ? (
        <EmptyState>Aucun terme de cette nature.</EmptyState>
      ) : null}

      {list.length > 0 ? (
        <Muted>
          {list.length} terme{list.length > 1 ? 's' : ''}
          {orphans > 0 ? ` · ${orphans} jamais employé${orphans > 1 ? 's' : ''}` : ''}
        </Muted>
      ) : null}

      <ul className={styles.rows}>
        {list.map((term) => (
          <TermRow key={term.id} term={term} siblings={list} onAct={act} />
        ))}
      </ul>
    </div>
  );
}

interface TermRowProps {
  term: ReferenceTermView;
  siblings: ReferenceTermView[];
  onAct: (action: () => Promise<unknown>) => Promise<void>;
}

function TermRow({ term, siblings, onAct }: TermRowProps): ReactNode {
  const api = useApi();
  const [mode, setMode] = useState<'idle' | 'rename' | 'merge'>('idle');
  const [label, setLabel] = useState(term.label);
  const [target, setTarget] = useState('');

  const others = siblings.filter((other) => other.id !== term.id);

  async function merge(): Promise<void> {
    const into = others.find((other) => other.id === target);
    if (!into) return;

    // La confirmation nomme ce qui va être réécrit, et combien. « Êtes-vous sûr ? » ne
    // dit rien de ce qu'on perd ; une fusion est sans retour et sans historique.
    const damage =
      term.usageCount > 0
        ? `${term.usageCount} fiche${term.usageCount > 1 ? 's ou notes seront réécrites' : ' ou note sera réécrite'}`
        : 'aucune fiche n’est concernée';
    if (
      !window.confirm(
        `Fusionner « ${term.label} » dans « ${into.label} » ?\n\n` +
          `${damage}. « ${term.label} » disparaîtra du référentiel.\n` +
          `Cette opération est sans retour.`,
      )
    ) {
      return;
    }

    setMode('idle');
    await onAct(() => api.reference.merge(term.id, into.id));
  }

  return (
    <li className={styles.row}>
      <span className={styles.rowInfo}>
        {mode === 'rename' ? (
          <Input
            autoFocus
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setMode('idle');
            }}
          />
        ) : (
          <strong>{term.label}</strong>
        )}
        <Muted>
          {term.family ? `${term.family} · ` : ''}
          {term.usageCount > 0
            ? `${term.usageCount} emploi${term.usageCount > 1 ? 's' : ''}`
            : 'jamais employé'}
        </Muted>
      </span>

      {mode === 'idle' ? (
        <>
          <Button variant="ghost" onClick={() => setMode('rename')}>
            Renommer
          </Button>
          {others.length > 0 ? (
            <Button variant="ghost" onClick={() => setMode('merge')}>
              Fusionner
            </Button>
          ) : null}
        </>
      ) : null}

      {mode === 'rename' ? (
        <>
          <Button
            disabled={label.trim().length < 2 || label.trim() === term.label}
            onClick={() => {
              setMode('idle');
              void onAct(() => api.reference.rename(term.id, label.trim()));
            }}
          >
            Enregistrer
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setLabel(term.label);
              setMode('idle');
            }}
          >
            Annuler
          </Button>
        </>
      ) : null}

      {mode === 'merge' ? (
        <Card className={styles.mergePanel}>
          <Muted>Absorber « {term.label} » dans :</Muted>
          <select
            className={styles.select}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          >
            <option value="">Choisir un terme…</option>
            {others.map((other) => (
              <option key={other.id} value={other.id}>
                {other.label}
                {other.usageCount > 0 ? ` (${other.usageCount})` : ''}
              </option>
            ))}
          </select>
          <div className={styles.adminActions}>
            <Button disabled={!target} onClick={() => void merge()}>
              Fusionner
            </Button>
            <Button variant="ghost" onClick={() => setMode('idle')}>
              Annuler
            </Button>
          </div>
        </Card>
      ) : null}
    </li>
  );
}
